<script setup>
import { onMounted, ref, onUnmounted } from 'vue';
import { videoManager } from '@/utils/videoManager';

import clsx from 'clsx';
import Image from 'primevue/image';

const isLoading = ref(false);
const props = defineProps({
  post: {
    type: Object,
    default: () => {},
  },
  src: {
    type: String,
    required: true,
  },
  classes: {
    type: String,
    default: '',
  },
  baseurl: {
    type: String,
    default: '',
  },
});

const videoRef = ref(null);
let observer = null;

onMounted(() => {
  if (props.post.mimetype.split('/')[0] === 'video' && videoRef.value) {
    // Add player to video manager
    videoManager.addPlayer(videoRef.value, videoRef.value);

    observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting && videoRef.value) {
            if (videoManager.isPlaying(videoRef.value)) {
              videoRef.value.pause();
            }
            if (videoManager.currentPlayer === videoRef.value) {
              videoManager.currentPlayer = null;
            }
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '0px',
      },
    );
    observer.observe(videoRef.value);

    videoRef.value.addEventListener('play', handleVideoPlay);
    videoRef.value.addEventListener('pause', handleVideoPause);
    videoRef.value.addEventListener('ended', handleVideoPause);
  }
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  if (videoRef.value) {
    videoRef.value.removeEventListener('play', handleVideoPlay);
    videoRef.value.removeEventListener('pause', handleVideoPause);
    videoRef.value.removeEventListener('ended', handleVideoPause);
    videoManager.removePlayer(videoRef.value);
  }
});

const handleVideoPlay = event => {
  const videoElement = event.target;
  videoManager.pauseAllVideos(videoElement);
  videoManager.currentPlayer = videoElement;
};

const handleVideoPause = event => {
  const videoElement = event.target;
  if (videoManager.currentPlayer === videoElement) {
    videoManager.currentPlayer = null;
  }
};

const test = () => {
  isLoading.value = true;
};
</script>

<template>
  <div @click="$emit('click')">
    <template v-if="post.mimetype.split('/')[0] === 'video'">
      <div v-if="post?.previewFile" class="video-player-placeholder">
        <img
          :src="baseurl + post.previewFile.storageId"
          alt=""
          class="post__image transition ease-in-out delay-150 cursor-pointer" />
      </div>
      <video
        v-else
        ref="videoRef"
        :src="src"
        controls
        class="post__video"
        draggable="false"
        ondragstart="return false;"
        playsinline
        @play="handleVideoPlay"
        @pause="handleVideoPause"
        @ended="handleVideoPause"></video>
    </template>

    <img
      v-else
      :src="src"
      alt="image of the post"
      class="post__image opacity-0 transition ease-in-out delay-150 cursor-pointer"
      :class="[{ 'opacity-100': isLoading }, classes]"
      @load="test" />
    <div v-if="!isLoading && post.mimetype.split('/')[0] !== 'video'" class="preloader"></div>
  </div>
</template>

<style scoped lang="scss">
.video-player-placeholder {
  position: relative;

  &:hover {
    &:after {
      transform: translate(-50%, -50%) scale(0.95);
    }

    &:before {
      transform: translate(-50%, -50%) scale(1.15);
    }
  }

  &:after {
    content: '';
    width: 0px;
    height: 0px;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: 5px;
    transform: translate(-50%, -50%) scale(0.8);
    transition: 0.3s;
    border-style: solid;
    border-width: 19.5px 0 19.5px 33.8px;
    pointer-events: none;
    border-color: transparent transparent transparent #fff;
  }

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    border-radius: 50%;
    transition: 0.3s;
    pointer-events: none;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 70px;
    height: 70px;
    background: #000;
    opacity: 0.3;
  }
}

.post__image {
  margin: 0 !important;
}

.full-height-image {
  max-height: 100vh;
  width: auto;
  object-fit: contain;
}
</style>
